import { useEffect, useMemo } from 'react';
import { useMachine } from '@xstate/react';
import { useApolloClient } from '@frameio/graphql-client';
import type { QuickSearchResult } from '@frameio/graphql-client/generated';
import { EMPTY_ARRAY } from '@frameio/utils';
import type { ListLoadingStates } from '@frameio/vapor';
import type { GetQuickSearchResultsV2QueryVariables } from '../../gql/generated/GetQuickSearchResultsV2';
import { quickSearchSingleMachine } from '../quickSearchSingleMachine';
import type { QuickSearchMachineOutput } from '../types';
import { useInitialSearchNodes } from './useInitialSearchNodes';

type UseQuickSearchMachineSingleProps = {
  requestVariables: GetQuickSearchResultsV2QueryVariables;
  skip: boolean;
  isSearchQueryStale: boolean;
  isInitial: boolean;
};

export const useQuickSearchMachineSingle = ({
  requestVariables,
  skip,
  isSearchQueryStale,
  isInitial,
}: UseQuickSearchMachineSingleProps): QuickSearchMachineOutput => {
  const apolloClient = useApolloClient();

  const { initialLexicalNodes } = useInitialSearchNodes({
    requestVariables,
    skip,
    isInitial,
    listMode: 'SINGLE',
  });

  const [state, send] = useMachine(quickSearchSingleMachine, {
    input: {
      apolloClient,
      requestVariables,
      initialLexicalNodes,
    },
  });

  useEffect(() => {
    if (skip || (isInitial && initialLexicalNodes !== undefined)) return;
    // Skip sending SEARCH if hydrating from cache (isInitial with cached data)
    send({ type: 'SEARCH', requestVariables });
  }, [requestVariables, send, skip, isInitial, initialLexicalNodes]);

  const loadingStatus = useMemo((): ListLoadingStates | undefined => {
    if (isSearchQueryStale || state.matches('loading')) {
      return 'loading';
    }

    const hasError = state.context.lexicalError !== undefined;
    if (state.matches('complete') && hasError) {
      // If the machine is in complete state and has an error, show loading-error
      return 'loading-error';
    }

    return undefined;
  }, [state, isSearchQueryStale]);

  // Build items from machine context
  const items = useMemo(() => {
    if (loadingStatus === 'loading') {
      return EMPTY_ARRAY;
    }

    return state.context.lexicalNodes;
  }, [state, loadingStatus]);

  const standardResultsCount = state.context.lexicalNodes.length;
  /**
   * The SEARCH event is dispatched from an effect, so on the render where
   * requestVariables changes, the machine's context/counts still reflect the
   * previous query for one render. Comparing context.requestVariables (set by
   * the SEARCH transition) to the current requestVariables prop closes that
   * gap so consumers never see "settled" paired with a stale query's counts.
   */
  const hasQueriesSettled =
    state.context.lexicalComplete &&
    state.context.requestVariables === requestVariables;

  return {
    listMode: 'SINGLE',
    items: items as QuickSearchResult[],
    loadingStatus,
    standardResultsCount,
    mediaIntelligenceResultsCount: 0,
    hasQueriesSettled,
  };
};
